import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";

import { getDeliverySlots } from "../../../../services/order";
import Modal from "../Modal";
import { REASONS_EVENTS } from "../../../../constants/reason";
import { getEventReasons } from "../../../../services/reason";
import Select from "../../../Select/Select";

const OrderSuccessModal = (props) => {
  const {
    comment,
    total,
    selectedDeliveryDate,
    onSelectDeliverySlot,
    isExchangeOrder,
    onIsExchangeOrderCheck,
    setFollowUpReason,
    followUpReason,
    setIsWaivedExpressDeliveryFees,
  } = props;

  const [deliveryDate, setDeliveryDate] = useState();
  const [additionalFees, setAdditionalFees] = useState();
  const [waivedReasons, setWaivedReasons] = useState([]);
  const [followUpReasons, setFollowUpReasons] = useState([]);

  const {
    id: mechanicId,
    name,
    selectedBranch: { branchId, branchName },
    shopName,
    primaryPhoneNumber,
  } = useSelector((state) => state.mechanic);
  const { isTaxable, createdFromOrderId, voiceOrderId } = useSelector(
    (state) => state.order
  );

  const { skus, bundles } = useSelector((state) => state.cart);
  const { selectedCar } = useSelector((state) => state.car);

  function getTimestampDisplay(timestamp) {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("ar", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function isSlotChecked(from, to) {
    return (
      selectedDeliveryDate &&
      selectedDeliveryDate.deliverySlotFrom === from &&
      selectedDeliveryDate.deliverySlotTo === to
    );
  }

  useEffect(() => {
    async function fetchDeliverySlots() {
      const requestData = {
        totalPrice: total,
        mechanicId,
        branchId,
        skuList: skus.map((sku) => ({
          skuId: sku.id,
          quantity: sku.quantity,
        })),
        bundleList: bundles.map((bundle) => ({
          id: bundle.id,
          requestedQuantity: bundle.quantity,
        })),
      };
      try {
        const res = await getDeliverySlots(requestData);
        const { data } = res;
        setDeliveryDate(data);
        setAdditionalFees(data?.additionalFees);
        setIsWaivedExpressDeliveryFees(
          selectedDeliveryDate?.isExpressDelivery
            ? data?.waiveExpressDeliveryFees
            : false
        );
      } catch (err) {
        console.error(err);
      }
    }
    fetchDeliverySlots();
  }, [
    mechanicId,
    branchId,
    skus,
    bundles,
    total,
    selectedDeliveryDate,
    setIsWaivedExpressDeliveryFees,
  ]);

  const fetchEventReasons = useCallback(async () => {
    try {
      const followUpResponse = await getEventReasons(
        REASONS_EVENTS.ORDER_FOLLOWING_UP
      );
      const waivedResponse = await getEventReasons(
        REASONS_EVENTS.ORDER_FOLLOWING_UP_WAIVED_ADDITIONAL_FEES
      );

      const {
        data: { data: followUpData },
      } = followUpResponse;

      const {
        data: { data: waivedData },
      } = waivedResponse;

      setFollowUpReasons(
        followUpData.map((reason) => ({
          value: reason.id,
          label: reason.name,
        }))
      );

      setWaivedReasons(
        waivedData.map((reason) => ({
          value: reason.id,
          label: (
            <div className="flex justify-between">
              <span>{reason.name}</span>
              <span class="bg-green-100 text-green-800 text-sm font-medium me-2 px-2.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                waived
              </span>
            </div>
          ),
        }))
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (createdFromOrderId) {
      fetchEventReasons();
    }
  }, [fetchEventReasons, createdFromOrderId]);

  const onSelectFollowUpReason = (reason) => {
    setFollowUpReason(reason);
  };

  const reasonsOptions = [...waivedReasons, ...followUpReasons];

  return (
    <Modal close={props.CloseOrderSummaryHandler}>
      <h3 className="text-center mb-6">ملخص الطلب</h3>
      <div className="flex flex-wrap justify-center sm:justify-between gap-3 items-center text-center text-sm px-6 mb-6">
        <p>
          <span className="font-bold">اسم العميل</span>
          <br />
          <span className="fw-bold">{name}</span>
        </p>

        <p>
          <span className="font-bold">اسم المحل</span>
          <br />
          <span className="fw-bold">{branchName || shopName || "-"}</span>
        </p>

        <p>
          <span className="font-bold">رقم الهاتف</span>
          <br />
          <span className="fw-bold">{primaryPhoneNumber}</span>
        </p>
      </div>

      <table className="w-full mt-3 text-center border text-sm mb-4">
        <thead>
          <tr>
            <th className="bg-slate-100">ماركة السيارة</th>
            <th className="bg-slate-100">موديل السيارة</th>
            <th className="bg-slate-100">السنة</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-2">{selectedCar.brand.brandName}</td>
            <td className="p-2">{selectedCar.model.modelName}</td>
            <td className="p-2">
              {selectedCar.year.startYear}-{selectedCar.year.endYear}
            </td>
          </tr>
        </tbody>
      </table>

      <table className="w-full mt-3 text-center border text-sm">
        <thead>
          <tr>
            <th className="bg-slate-100">اسم القطعة</th>
            <th className="bg-slate-100">الكمية</th>
          </tr>
        </thead>
        <tbody>
          {skus.map((sku, index) => (
            <tr key={`${index}_${sku.id}`}>
              <td className="p-2">{sku.commercialName || sku.name}</td>
              <td className="p-2">{sku.quantity}</td>
            </tr>
          ))}

          {bundles.flatMap((bundle) =>
            bundle.skuList.map((sku, index) => (
              <tr key={`${index}-${sku.id}`}>
                <td className="p-2">{sku.commercialName || sku.name}</td>
                <td className="p-2">{sku.quantity}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {comment && (
        <div className="mt-4 text-center">
          <p className="font-bold">ملاحظات خاصة</p>
          <p>{comment}</p>
        </div>
      )}

      {voiceOrderId && (
        <div className="mt-4 text-center">
          <p className="font-bold">رقم الطلب الصوتي</p>
          <p>{voiceOrderId}</p>
        </div>
      )}

      {isTaxable && (
        <p className="text-center text-sm font-bold mt-4">
          الطلب بفاتورة ضريبية
        </p>
      )}

      {createdFromOrderId && (
        <div className="w-2/3 my-4">
          <label className="mb-2"> Follow Up Resason </label>
          <Select
            placeholder="Select reason"
            options={reasonsOptions}
            optionLabelProp="label"
            onSelect={onSelectFollowUpReason}
            initialValue={followUpReason?.label}
          />
        </div>
      )}

      <hr className="my-4" />

      <div className="mt-1">
        <h2 className="text-lg text-center mb-4">أختر ميعاد التوصيل</h2>

        {deliveryDate?.expressDelivery && (
          <div className="border-b-2 pb-2">
            <div className="flex gap-2 items-center mb-1">
              <input
                id={`${deliveryDate.expressDelivery.from}_${deliveryDate.expressDelivery.to}`}
                type="radio"
                name="express"
                value={JSON.stringify({
                  from: deliveryDate.expressDelivery.from,
                  to: deliveryDate.expressDelivery.to,
                  slaInMinutes: deliveryDate.expressDelivery.slaInMinutes,
                  serviceFees: deliveryDate.expressDelivery.serviceFees,
                })}
                onChange={onSelectDeliverySlot}
                checked={isSlotChecked(
                  deliveryDate.expressDelivery.from,
                  deliveryDate.expressDelivery.to
                )}
              />
              <label
                htmlFor={`${deliveryDate.expressDelivery.from}_${deliveryDate.expressDelivery.to}`}
                className="cursor-pointer"
              >
                في أقرب وقت
                <strong className="inline-block mx-2">
                  {new Date(
                    deliveryDate.expressDelivery.from
                  ).toLocaleDateString("ar", {
                    weekday: "long",
                    day: "numeric",
                    month: "numeric",
                  })}
                </strong>
                من
                <strong className="inline-block mr-2">
                  {getTimestampDisplay(deliveryDate.expressDelivery.from)} -{" "}
                  {getTimestampDisplay(deliveryDate.expressDelivery.to)}
                </strong>
              </label>
            </div>
            {deliveryDate.waiveExpressDeliveryFees ? (
              <p className="text-sm  text-red-800 font-black">
                ليك مره توصيل سريع ببلاش ! 🎉
              </p>
            ) : (
              <p className="text-sm text-red-800 font-black">
                يرجي العلم أن مصاريف الشحن {deliveryDate.expressDelivery.fees}{" "}
                جنيه
              </p>
            )}
            {deliveryDate.expressDelivery.serviceFees > 0 && (
              <p className="text-sm text-red-800 font-black">
                رسوم الخدمة: {deliveryDate.expressDelivery.serviceFees} جنيه
              </p>
            )}
          </div>
        )}
        <div className="mt-2">
          {deliveryDate?.expressDelivery && (
            <h2 className="text-base mb-1">تحديد ميعاد آخر</h2>
          )}
          <ul className="flex gap-2 flex-wrap items-start">
            {deliveryDate?.scheduledDelivery.map((day, index) => (
              <li key={index} className="border p-4 rounded-lg">
                <h3 className="text-sm text-center mb-1">{day.day}</h3>
                <ul className="flex flex-col gap-2">
                  {day.slots.map((slot, index) => (
                    <li key={index} className="flex gap-2 items-center">
                      <input
                        id={`${slot.from}_${slot.to}`}
                        type="radio"
                        name="scheduled"
                        value={JSON.stringify(slot)}
                        onChange={onSelectDeliverySlot}
                        checked={isSlotChecked(slot.from, slot.to)}
                      />
                      <label
                        htmlFor={`${slot.from}_${slot.to}`}
                        className="cursor-pointer"
                      >
                        من {getTimestampDisplay(slot.from)} -{" "}
                        {getTimestampDisplay(slot.to)}
                      </label>

                      {slot.serviceFees && slot.serviceFees > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800">
                          رسوم الخدمة : {slot.serviceFees} جم
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="flex flex-col w-1/2 gap-2 mt-5">
        <div className="flex justify-between ">
          <span>الإجمالي</span>
          <span> {total} جم</span>
        </div>
        <div className="flex justify-between ">
          {selectedDeliveryDate?.isExpressDelivery &&
          deliveryDate?.waiveExpressDeliveryFees ? (
            <>
              <div className="w-full flex justify-between ">
                <span className="relative text-black text-lg">
                  رسوم التوصيل
                  <span className="absolute left-0 right-0 top-1/2 h-px bg-gray-700"></span>
                </span>
                <span className="relative text-black text-lg">
                  {selectedDeliveryDate?.isExpressDelivery
                    ? deliveryDate?.expressDelivery?.fees
                    : 0}{" "}
                  جم
                  <span className="absolute left-0 right-0 top-1/2 h-px bg-gray-700"></span>
                </span>
              </div>
            </>
          ) : (
            <>
              <span> رسوم التوصيل </span>
              <span>
                {" "}
                {selectedDeliveryDate?.isExpressDelivery
                  ? deliveryDate?.expressDelivery?.fees
                  : 0}{" "}
                جم
              </span>
            </>
          )}
        </div>

        {selectedDeliveryDate && selectedDeliveryDate.serviceFees > 0 && (
          <div className="flex justify-between ">
            <span>رسوم الخدمة</span>
            <span>{selectedDeliveryDate.serviceFees} جم</span>
          </div>
        )}

        {additionalFees?.minimumThreshold && (
          <div className="flex justify-between ">
            <span> رسوم طلب أقل من {additionalFees?.minimumThreshold} </span>
            <span> {additionalFees?.fees} جم</span>
          </div>
        )}

        {additionalFees?.isWaived && (
          <div className="flex justify-between ">
            <span> خصم </span>
            <span> {additionalFees?.fees} جم</span>
          </div>
        )}

        <div className="flex justify-between pt-2 border-t border-gray-300 text-[#4d7c0f] font-bold">
          <span>الإجمالي</span>
          <span>
            {total +
              (selectedDeliveryDate?.isExpressDelivery &&
              !deliveryDate?.waiveExpressDeliveryFees
                ? deliveryDate?.expressDelivery?.fees
                : 0) +
              (additionalFees?.isWaived ? 0 : additionalFees?.fees || 0) +
              (selectedDeliveryDate?.serviceFees || 0)}{" "}
            جم
          </span>
        </div>
      </div>

      {createdFromOrderId && (
        <div
          dir="ltr"
          className="my-4 flex justify-center gap-4 bg-slate-100 rounded-md py-2"
        >
          <label htmlFor="isExchangeOrder">Is Exchange Order ?</label>
          <input
            id="isExchangeOrder"
            type="checkbox"
            checked={isExchangeOrder}
            onChange={onIsExchangeOrderCheck}
          />
        </div>
      )}

      <div className="flex justify-between mt-16">
        <button
          className="p-2 bg-dark-blue text-white font-bold text-sm rounded"
          disabled={
            !selectedDeliveryDate ||
            (createdFromOrderId ? !followUpReason : followUpReason)
          }
          onClick={props.createOrderHandler}
        >
          تاكيد
        </button>
        <button
          className="p-2 bg-light-gray font-bold text-sm rounded"
          onClick={props.CloseOrderSummaryHandler}
        >
          إلغاء
        </button>
      </div>
    </Modal>
  );
};

export default OrderSuccessModal;
